import { useState, useEffect, useCallback, useRef, memo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Keyboard, Star, Home, Trophy, Map } from 'lucide-react';
import axios from 'axios';
import { API, useAuth } from '../App';
import BlockyCharacter3D from '../components/BlockyCharacter3D';
import { BUILDING_SVG_MAP } from '../components/BuildingSVGs';
import { BIOME_ASSET_MAP } from '../components/BiomeAssets';
import TypingChallenge from '../components/island/TypingChallenge';
import NPCInteraction from '../components/island/NPCInteraction';
import OnboardingTour from '../components/island/OnboardingTour';
import StageCompletionQuiz from '../components/island/StageCompletionQuiz';
import LevelCelebration from '../components/island/LevelCelebration';
import StageCelebration from '../components/island/StageCelebration';
import BuildMenu from '../components/island/BuildMenu';
import BuildingChallenge from '../components/island/BuildingChallenge';
import Sky from '../components/island/Sky';
import Ground from '../components/island/Ground';
import MobileDPad from '../components/island/MobileDPad';
import MiniMap from '../components/island/MiniMap';
import UpgradePrompt from '../components/island/UpgradePrompt';
import { usePlayerMovement } from '../hooks/usePlayerMovement';
import { useGameClock } from '../hooks/useGameClock';
import { NPC_DATA, NPC_STYLES } from '../data/npcData';
import { ZONES, ISLAND_SIZE, getZone, isZoneUnlocked, STAGE_ICONS } from '../data/zoneData';
import { ALL_BUILDINGS, TYPE_TO_LEVEL_MAP, getUnlockedBuildings, BUILDINGS } from '../data/buildingsData';
import { WORLD_OBJECTS, INITIAL_WORLD_OBJECTS, generateWorldObjects } from '../data/worldObjects';


// ============================================
// WorldObject Component - renders individual world objects
// ============================================
const WorldObject = memo(({ type, screenX, screenY, scale, opacity, zIndex, npcId, buildingId, onRemove, npcPosition, onBuildingClick }) => {
  const isUserBuilding = !!buildingId;
  const baseStyle = { transform: `scale(${scale})`, opacity: 1 };

  if (type === 'rock') {
    const rw = Math.round(50 * scale);
    const rh = Math.round(40 * scale);
    return (
      <div>
        <svg width={rw} height={rh} viewBox="0 0 50 40">
          <polygon points="5,40 0,25 10,10 25,5 40,8 50,20 45,40" fill="#696969" />
          <polygon points="10,30 8,20 20,12 35,15 38,28" fill="#808080" />
        </svg>
      </div>
    );
  }
  
  if (type === 'bush') {
    const bw = Math.round(50 * scale);
    const bh = Math.round(35 * scale);
    return (
      <div>
        <svg width={bw} height={bh} viewBox="0 0 50 35">
          <ellipse cx="25" cy="25" rx="23" ry="15" fill="#228B22" />
          <ellipse cx="15" cy="18" rx="12" ry="10" fill="#2E8B2E" />
          <ellipse cx="35" cy="20" rx="10" ry="8" fill="#1E7B1E" />
        </svg>
      </div>
    );
  }
  
  if (type === 'tree') {
    const tw = Math.round(160 * scale);
    const th = Math.round(240 * scale);
    return (
      <div data-testid="tree-obstacle">
        <svg width={tw} height={th} viewBox="0 0 80 120">
          <ellipse cx="40" cy="118" rx="25" ry="6" fill="rgba(0,0,0,0.3)" />
          <rect x="32" y="70" width="16" height="50" fill="#8B4513" />
          <rect x="30" y="70" width="4" height="50" fill="#6B3410" />
          <rect x="46" y="70" width="4" height="50" fill="#A0522D" />
          <line x1="35" y1="75" x2="35" y2="115" stroke="#5D3A1A" strokeWidth="1" />
          <line x1="40" y1="80" x2="40" y2="118" stroke="#5D3A1A" strokeWidth="1" />
          <line x1="45" y1="75" x2="45" y2="115" stroke="#5D3A1A" strokeWidth="1" />
          <rect x="10" y="50" width="60" height="30" rx="4" fill="#228B22" />
          <rect x="8" y="50" width="8" height="30" rx="2" fill="#1E6B1E" />
          <rect x="64" y="50" width="8" height="30" rx="2" fill="#2E8B2E" />
          <rect x="15" y="30" width="50" height="28" rx="4" fill="#2E8B57" />
          <rect x="13" y="30" width="6" height="28" rx="2" fill="#1E6B47" />
          <rect x="61" y="30" width="6" height="28" rx="2" fill="#3E9B67" />
          <rect x="22" y="12" width="36" height="26" rx="4" fill="#32CD32" />
          <rect x="20" y="12" width="5" height="26" rx="2" fill="#22AD22" />
          <rect x="55" y="12" width="5" height="26" rx="2" fill="#42DD42" />
          <rect x="30" y="5" width="20" height="14" rx="3" fill="#3CB371" />
        </svg>
      </div>
    );
  }
  
  if (type === 'pine') {
    const tw = Math.round(120 * scale);
    const th = Math.round(280 * scale);
    return (
      <div data-testid="pine-obstacle">
        <svg width={tw} height={th} viewBox="0 0 60 140">
          <ellipse cx="30" cy="138" rx="20" ry="5" fill="rgba(0,0,0,0.3)" />
          <rect x="24" y="100" width="12" height="40" fill="#8B4513" />
          <rect x="22" y="100" width="4" height="40" fill="#6B3410" />
          <rect x="34" y="100" width="4" height="40" fill="#A0522D" />
          <polygon points="30,5 5,50 55,50" fill="#2E8B57" />
          <polygon points="30,5 5,50 20,50" fill="#1E6B47" />
          <polygon points="30,30 8,70 52,70" fill="#228B22" />
          <polygon points="30,30 8,70 18,70" fill="#186B18" />
          <polygon points="30,55 10,95 50,95" fill="#1E7B1E" />
          <polygon points="30,55 10,95 18,95" fill="#0E5B0E" />
        </svg>
      </div>
    );
  }
  
  if (type === 'tent') {
    return (
      <div className={isUserBuilding ? 'hover:brightness-110 transition-all' : ''} style={baseStyle}>
        <svg width="180" height="150" viewBox="0 0 90 70">
          <ellipse cx="45" cy="68" rx="40" ry="4" fill="rgba(0,0,0,0.3)" />
          <polygon points="45,5 5,65 85,65" fill="#F5DEB3" stroke="#D2B48C" strokeWidth="2" />
          <polygon points="45,5 5,65 45,65" fill="#E8D4A8" />
          <polygon points="45,35 30,65 60,65" fill="#4a3520" />
          <polygon points="45,35 35,65 55,65" fill="#2a1a10" />
          <line x1="45" y1="0" x2="45" y2="10" stroke="#8B4513" strokeWidth="3" />
          <line x1="45" y1="5" x2="10" y2="60" stroke="#8B7355" strokeWidth="1" strokeDasharray="3,3" />
          <line x1="45" y1="5" x2="80" y2="60" stroke="#8B7355" strokeWidth="1" strokeDasharray="3,3" />
        </svg>
      </div>
    );
  }
  
  if (type === 'hut') {
    return (
      <div className={isUserBuilding ? 'hover:brightness-110 transition-all' : ''} style={baseStyle}>
        <svg width="140" height="150" viewBox="0 0 80 85">
          <ellipse cx="40" cy="82" rx="35" ry="4" fill="rgba(0,0,0,0.3)" />
          <rect x="10" y="40" width="60" height="42" fill="#DEB887" stroke="#8B4513" strokeWidth="2" />
          <line x1="10" y1="50" x2="70" y2="50" stroke="#C4A76C" strokeWidth="1" />
          <line x1="10" y1="60" x2="70" y2="60" stroke="#C4A76C" strokeWidth="1" />
          <line x1="10" y1="70" x2="70" y2="70" stroke="#C4A76C" strokeWidth="1" />
          <polygon points="40,5 0,45 80,45" fill="#DAA520" stroke="#B8860B" strokeWidth="2" />
          <line x1="20" y1="35" x2="40" y2="10" stroke="#C4A000" strokeWidth="1" />
          <line x1="30" y1="40" x2="40" y2="15" stroke="#C4A000" strokeWidth="1" />
          <line x1="50" y1="40" x2="40" y2="15" stroke="#C4A000" strokeWidth="1" />
          <line x1="60" y1="35" x2="40" y2="10" stroke="#C4A000" strokeWidth="1" />
          <rect x="32" y="52" width="16" height="30" fill="#5D3A1A" stroke="#3D2A0A" strokeWidth="1" rx="2" />
          <circle cx="44" cy="67" r="2" fill="#FFD700" />
          <rect x="52" y="50" width="12" height="12" fill="#87CEEB" stroke="#5D3A1A" strokeWidth="1" />
          <line x1="58" y1="50" x2="58" y2="62" stroke="#5D3A1A" strokeWidth="1" />
          <line x1="52" y1="56" x2="64" y2="56" stroke="#5D3A1A" strokeWidth="1" />
        </svg>
      </div>
    );
  }
  
  if (type === 'house') {
    return (
      <div className={isUserBuilding ? 'hover:brightness-110 transition-all' : ''} style={baseStyle}>
        <svg width="160" height="160" viewBox="0 0 100 100">
          <ellipse cx="50" cy="97" rx="45" ry="4" fill="rgba(0,0,0,0.3)" />
          <rect x="70" y="15" width="12" height="30" fill="#8B0000" stroke="#600" strokeWidth="1" />
          <rect x="68" y="12" width="16" height="5" fill="#8B0000" stroke="#600" strokeWidth="1" />
          <rect x="10" y="40" width="80" height="55" fill="#CD853F" stroke="#8B4513" strokeWidth="2" />
          <g fill="none" stroke="#B8733F" strokeWidth="0.5">
            <line x1="10" y1="50" x2="90" y2="50" /><line x1="10" y1="60" x2="90" y2="60" />
            <line x1="10" y1="70" x2="90" y2="70" /><line x1="10" y1="80" x2="90" y2="80" />
            <line x1="30" y1="40" x2="30" y2="50" /><line x1="50" y1="40" x2="50" y2="50" />
            <line x1="70" y1="40" x2="70" y2="50" />
          </g>
          <polygon points="50,5 0,45 100,45" fill="#B22222" stroke="#8B0000" strokeWidth="2" />
          <line x1="25" y1="25" x2="50" y2="8" stroke="#8B0000" strokeWidth="1" />
          <line x1="75" y1="25" x2="50" y2="8" stroke="#8B0000" strokeWidth="1" />
          <rect x="40" y="55" width="20" height="40" fill="#5D3A1A" stroke="#3D2A0A" strokeWidth="2" rx="2" />
          <rect x="42" y="57" width="7" height="15" fill="#4a2a10" rx="1" />
          <rect x="51" y="57" width="7" height="15" fill="#4a2a10" rx="1" />
          <circle cx="55" cy="78" r="2" fill="#FFD700" />
          <rect x="15" y="50" width="18" height="18" fill="#87CEEB" stroke="#5D3A1A" strokeWidth="2" />
          <line x1="24" y1="50" x2="24" y2="68" stroke="#5D3A1A" strokeWidth="1" />
          <line x1="15" y1="59" x2="33" y2="59" stroke="#5D3A1A" strokeWidth="1" />
          <rect x="67" y="50" width="18" height="18" fill="#87CEEB" stroke="#5D3A1A" strokeWidth="2" />
          <line x1="76" y1="50" x2="76" y2="68" stroke="#5D3A1A" strokeWidth="1" />
          <line x1="67" y1="59" x2="85" y2="59" stroke="#5D3A1A" strokeWidth="1" />
        </svg>
      </div>
    );
  }
  
  if (type === 'office') {
    return (
      <div className={isUserBuilding ? 'hover:brightness-125 transition-all' : ''} style={baseStyle}>
        <svg width="140" height="190" viewBox="0 0 80 110">
          <ellipse cx="40" cy="107" rx="35" ry="4" fill="rgba(0,0,0,0.3)" />
          <rect x="5" y="15" width="70" height="90" fill="#607D8B" stroke="#455A64" strokeWidth="2" />
          <g fill="#87CEEB" stroke="#455A64" strokeWidth="1">
            <rect x="10" y="20" width="12" height="15" /><rect x="27" y="20" width="12" height="15" />
            <rect x="44" y="20" width="12" height="15" /><rect x="61" y="20" width="12" height="15" />
            <rect x="10" y="40" width="12" height="15" /><rect x="27" y="40" width="12" height="15" />
            <rect x="44" y="40" width="12" height="15" /><rect x="61" y="40" width="12" height="15" />
            <rect x="10" y="60" width="12" height="15" /><rect x="27" y="60" width="12" height="15" />
            <rect x="44" y="60" width="12" height="15" /><rect x="61" y="60" width="12" height="15" />
          </g>
          <rect x="25" y="80" width="30" height="25" fill="#5D9CEC" stroke="#455A64" strokeWidth="2" />
          <line x1="40" y1="80" x2="40" y2="105" stroke="#455A64" strokeWidth="1" />
          <rect x="3" y="10" width="74" height="8" fill="#37474F" stroke="#263238" strokeWidth="1" />
        </svg>
      </div>
    );
  }
  
  if (type === 'campfire') {
    return (
      <div style={baseStyle}>
        <svg width="160" height="160" viewBox="0 0 50 50">
          <ellipse cx="25" cy="45" rx="20" ry="5" fill="#5D3A1A" />
          <rect x="8" y="38" width="8" height="12" fill="#8B4513" transform="rotate(-20, 12, 44)" />
          <rect x="34" y="38" width="8" height="12" fill="#8B4513" transform="rotate(20, 38, 44)" />
          <ellipse cx="25" cy="30" rx="12" ry="20" fill="#FF6600" opacity="0.9" />
          <ellipse cx="25" cy="25" rx="8" ry="15" fill="#FFD700" opacity="0.8" />
          <ellipse cx="25" cy="20" rx="4" ry="10" fill="#FFFF00" opacity="0.7" />
        </svg>
      </div>
    );
  }
  
  // SVG-based building from BuildingSVGs
  const buildingLevel = TYPE_TO_LEVEL_MAP[type];
  if (buildingLevel && BUILDING_SVG_MAP[buildingLevel]) {
    const BuildingSVGComponent = BUILDING_SVG_MAP[buildingLevel];
    return (
      <div className={isUserBuilding ? 'hover:brightness-110 transition-all cursor-pointer' : ''} style={baseStyle}>
        <BuildingSVGComponent scale={scale} isUserBuilding={isUserBuilding} />
      </div>
    );
  }
  
  // NPC Human Characters
  if (type === 'npc') {
    const style = NPC_STYLES[npcId] || NPC_STYLES.farmer;
    const npcSize = Math.round(55 * scale);
    return (
      <div className="cursor-pointer animate-idle-bob" style={{ position: 'relative' }} data-npc-id={npcId} data-testid={`npc-${npcId}`}>
        <BlockyCharacter3D
          size={npcSize}
          skinTone={style.skinTone}
          hairColor={style.hairColor}
          hairStyle={style.hairStyle}
          shirtColor={style.shirtColor}
          pantsColor={style.pantsColor}
          eyeStyle="normal"
          mouthStyle="smile"
          accessory={style.accessory}
          direction="down"
          isMoving={false}
          showNameTag={true}
          name={style.name}
          showSpeechBubble={true}
        />
      </div>
    );
  }
  
  // Biome assets
  const BiomeComponent = BIOME_ASSET_MAP[type];
  if (BiomeComponent) {
    const assetSize = Math.round(60 * scale);
    return <div style={baseStyle}><BiomeComponent size={assetSize} /></div>;
  }
  
  return null;
});

// ============================================
// Main Island Page
// ============================================
const IslandPage = () => {
  const { user } = useAuth();
  const [island, setIsland] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // World objects (decorations + user buildings) - initialized with NPCs
  const [worldObjects, setWorldObjects] = useState(INITIAL_WORLD_OBJECTS);
  
  // NPC positions (separate from static world objects)
  const [npcPositions, setNpcPositions] = useState(() => {
    const npcs = {};
    INITIAL_WORLD_OBJECTS.filter(obj => obj.type === 'npc').forEach(npc => {
      npcs[npc.id] = { x: npc.x, y: npc.y, targetX: npc.x, targetY: npc.y, moving: false };
    });
    return npcs;
  });
  const npcPositionsRef = useRef(npcPositions);
  useEffect(() => { npcPositionsRef.current = npcPositions; }, [npcPositions]);

  // UI state
  const [showChallenge, setShowChallenge] = useState(false);
  const [currentChallenge, setCurrentChallenge] = useState(null);
  const [showBuildMenu, setShowBuildMenu] = useState(false);
  const [showMobileMap, setShowMobileMap] = useState(false);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [selectedBuildingForRemoval, setSelectedBuildingForRemoval] = useState(null);
  const [message, setMessage] = useState('');
  const [activeNpc, setActiveNpc] = useState(null);
  const [activeBuilding, setActiveBuilding] = useState(null);
  const [showStageQuiz, setShowStageQuiz] = useState(false);
  const [quizStage, setQuizStage] = useState(1);
  const [previousStage, setPreviousStage] = useState(1);
  const [showStageSelector, setShowStageSelector] = useState(false);
  const [selectedPracticeStage, setSelectedPracticeStage] = useState(null);
  
  // Celebration states
  const [showLevelCelebration, setShowLevelCelebration] = useState(false);
  const [levelCelebrationData, setLevelCelebrationData] = useState(null);
  const [showStageCelebration, setShowStageCelebration] = useState(false);
  const [stageCelebrationData, setStageCelebrationData] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  
  // Pixel customization
  const [pixelData, setPixelData] = useState({
    color: '#00FFD0', bodyMetal: 'silver', eyes: 'dot', mouth: 'smile',
    body: 'round', head: 'round', hat: 'none', arms: 'normal',
    skinTone: '#FFE0BD', hairColor: '#4a3728', hairStyle: 'short',
    shirtColor: '#4A90D9', pantsColor: '#3B82F6', shoes: 'trainers', eyeStyle: 'normal',
    mouthStyle: 'smile', accessory: 'none',
  });
  
  // NPC original positions
  const npcOriginalPositions = useRef({});
  useEffect(() => {
    INITIAL_WORLD_OBJECTS.filter(obj => obj.type === 'npc').forEach(npc => {
      npcOriginalPositions.current[npc.id] = { x: npc.x, y: npc.y };
    });
  }, []);
  
  // Current player stage
  const currentStage = island?.level_info?.stage || 1;
  
  // Message ref to avoid re-renders during movement
  const messageTimeoutRef = useRef(null);
  
  // === HOOKS ===
  
  // Day/night cycle via useGameClock
  const { timeOfDay, weather } = useGameClock();
  
  // Player movement via usePlayerMovement
  const {
    playerPos, direction, isMoving, setPlayerPos, setDirection, setIsMoving, keysPressed,
  } = usePlayerMovement({
    moveSpeed: 48,
    disabled: showChallenge || !!activeNpc,
    currentStage,
    worldObjects,
    onZoneLocked: (zone) => {
      if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
      setMessage(`🔒 ${zone.name} requires Stage ${zone.stage}!`);
      messageTimeoutRef.current = setTimeout(() => setMessage(''), 1500);
    },
  });
  
  // === DATA FETCHING ===
  
  const fetchIsland = useCallback(async () => {
    try {
      const token = localStorage.getItem('typetown_token');
      const res = await axios.get(`${API}/island`, { headers: { Authorization: `Bearer ${token}` } });
      setIsland(res.data);
      
      const stage = res.data?.level_info?.stage || 1;
      const scaledObjects = generateWorldObjects(stage);
      
      if (res.data.buildings?.length > 0) {
        const stageMultiplier = Math.pow(10, Math.max(0, stage - 1));
        const userBuildings = res.data.buildings.map((b, i) => ({
          id: 2000 + i,
          type: b.building_type,
          x: b.x * 40 * stageMultiplier,
          y: b.y * 40 * stageMultiplier - 150 * stageMultiplier,
          buildingId: b.id,
        }));
        const nonNpcObjects = scaledObjects.filter(obj => obj.type !== 'npc');
        const npcObjects = scaledObjects.filter(obj => obj.type === 'npc');
        setWorldObjects([...nonNpcObjects, ...npcObjects, ...userBuildings]);
      } else {
        setWorldObjects([...scaledObjects]);
      }
      
      const npcs = {};
      scaledObjects.filter(obj => obj.type === 'npc').forEach(npc => {
        npcs[npc.id] = { x: npc.x, y: npc.y, targetX: npc.x, targetY: npc.y, moving: false };
      });
      setNpcPositions(npcs);
    } catch (e) {}
    setLoading(false);
  }, []);
  
  useEffect(() => {
    fetchIsland();
    const fetchPixel = async () => {
      try {
        const token = localStorage.getItem('typetown_token');
        const [pixelRes, charRes] = await Promise.all([
          axios.get(`${API}/pixel`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
          axios.get(`${API}/character`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        ]);
        setPixelData({
          color: pixelRes?.data?.pixel_color || '#00FFD0',
          bodyMetal: pixelRes?.data?.pixel_body_metal || 'silver',
          eyes: pixelRes?.data?.pixel_eyes || 'dot',
          mouth: pixelRes?.data?.pixel_mouth || 'smile',
          body: pixelRes?.data?.pixel_body || 'round',
          head: pixelRes?.data?.pixel_head || 'round',
          hat: pixelRes?.data?.pixel_hat || 'none',
          arms: pixelRes?.data?.pixel_arms || 'normal',
          skinTone: charRes?.data?.character_skin_tone || '#FFE0BD',
          hairColor: charRes?.data?.character_hair_color || '#4a3728',
          hairStyle: charRes?.data?.character_hair_style || 'short',
          shirtColor: charRes?.data?.character_shirt_color || '#4A90D9',
          pantsColor: charRes?.data?.character_pants_color || '#3B82F6',
          shoes: charRes?.data?.character_shoes || 'trainers',
          eyeStyle: charRes?.data?.character_eye_style || 'normal',
          mouthStyle: charRes?.data?.character_mouth_style || 'smile',
          accessory: charRes?.data?.character_accessory || 'none',
        });
      } catch (e) {}
    };
    fetchPixel();
  }, [fetchIsland]);
  
  // === CHALLENGE HANDLERS ===
  
  const startChallenge = async (practiceStage = null) => {
    try {
      const token = localStorage.getItem('typetown_token');
      const url = practiceStage 
        ? `${API}/island/challenge?practice_stage=${practiceStage}`
        : `${API}/island/challenge`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setCurrentChallenge(res.data);
      setSelectedPracticeStage(practiceStage);
      setShowChallenge(true);
      setShowStageSelector(false);
    } catch (e) {}
  };
  
  const openStageSelector = () => {
    const currentStage = island?.level_info?.stage || 1;
    if (currentStage > 1) {
      setShowStageSelector(true);
    } else {
      // Only one stage available, start directly
      startChallenge();
    }
  };
  
  const completeChallenge = async (data) => {
    try {
      const token = localStorage.getItem('typetown_token');
      const currentStageBefore = island?.level_info?.stage || 1;
      const res = await axios.post(`${API}/island/challenge/complete`, data, { headers: { Authorization: `Bearer ${token}` } });
      
      if (res.data.success) {
        const levelCompleted = res.data.level_completed;
        const newStage = res.data.level_info?.stage || currentStageBefore;
        const stageChanged = res.data.stage_changed;
        
        if (levelCompleted) {
          setShowChallenge(false);
          const newBuilding = res.data.new_building_unlocked ? { name: res.data.new_building_unlocked, emoji: '🏠' } : null;
          setLevelCelebrationData({
            level: res.data.level_info?.level || 1,
            stage: newStage,
            stage_name: res.data.level_info?.stage_name || 'Campsite',
            credits_earned: res.data.credits_earned || 10,
            new_building: newBuilding,
          });
          setShowLevelCelebration(true);
          
          if (stageChanged && newStage > 1) {
            setTimeout(() => {
              setStageCelebrationData({ new_stage: newStage, stage_name: res.data.level_info?.stage_name || 'Hamlet', unlocked_zone: newStage });
              setShowStageCelebration(true);
            }, 500);
          }
        } else {
          const phrasesTyped = res.data.phrases_typed || 0;
          const phrasesNeeded = res.data.phrases_needed || 10;
          try {
            const nextRes = await axios.get(`${API}/island/challenge`, { headers: { Authorization: `Bearer ${token}` } });
            setCurrentChallenge(nextRes.data);
          } catch (e) {
            setShowChallenge(false);
            setMessage(`✓ ${phrasesTyped}/${phrasesNeeded} phrases typed! Keep going!`);
          }
        }
        fetchIsland();
      } else {
        setMessage(`✗ ${res.data.accuracy}% accuracy - Need 80% to pass!`);
      }
      setTimeout(() => setMessage(''), 4000);
    } catch (e) {
      console.error('Challenge completion error:', e);
    }
  };
  
  const closeLevelCelebration = () => { setShowLevelCelebration(false); setLevelCelebrationData(null); };
  
  const nextLevelFromCelebration = () => {
    setShowLevelCelebration(false);
    setLevelCelebrationData(null);
    startChallenge(selectedPracticeStage);
  };
  const closeStageCelebration = () => { setShowStageCelebration(false); setStageCelebrationData(null); };
  
  const handleQuizComplete = (score, total) => {
    setShowStageQuiz(false);
    const percentage = Math.round((score / total) * 100);
    if (percentage >= 60) setMessage(`🎓 Quiz Complete! ${score}/${total} correct! New zone unlocked!`);
    else setMessage(`📚 Quiz done! ${score}/${total} - Talk to more characters to learn!`);
    setTimeout(() => setMessage(''), 5000);
  };
  
  // === BUILDING HANDLERS ===
  
  const placeBuilding = async (worldX = null, worldY = null) => {
    if (!selectedBuilding) return;
    let buildingInfo = Object.values(ALL_BUILDINGS).find(b => b.type === selectedBuilding);
    if (!buildingInfo) buildingInfo = BUILDINGS[selectedBuilding];
    const buildingCost = buildingInfo?.cost || 0;
    const userCredits = island?.credits || 0;
    
    if (userCredits < buildingCost) {
      setMessage(`Not enough coins! You need ${buildingCost} coins (you have ${userCredits})`);
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    try {
      const token = localStorage.getItem('typetown_token');
      const targetX = worldX !== null ? worldX : playerPos.x;
      const targetY = worldY !== null ? worldY : playerPos.y - 100;
      const buildX = Math.round(targetX / 40);
      const buildY = Math.round(targetY / 40);
      
      const res = await axios.post(`${API}/island/building`, { building_type: selectedBuilding, x: buildX, y: buildY }, { headers: { Authorization: `Bearer ${token}` } });
      
      if (res.data?.building) {
        setMessage(`🏗️ Built ${buildingInfo?.name || selectedBuilding}! (-${buildingCost} coins)`);
        setSelectedBuilding(null);
        setDraggingBuilding(null);
        
        const newObj = { id: 2000 + Date.now(), type: selectedBuilding, x: targetX, y: targetY, buildingId: res.data.building.id };
        setWorldObjects(prev => [...prev, newObj]);
        setIsland(prev => ({ ...prev, credits: (prev?.credits || 0) - buildingCost, buildings: [...(prev?.buildings || []), res.data.building] }));
      }
      setTimeout(() => setMessage(''), 3000);
    } catch (e) {
      const errorMsg = e.response?.data?.detail || 'Could not place building here';
      setMessage(`${errorMsg}`);
      setTimeout(() => setMessage(''), 2000);
    }
  };
  
  const removeBuilding = async (buildingId) => {
    if (!buildingId) return;
    try {
      const token = localStorage.getItem('typetown_token');
      await axios.delete(`${API}/island/building/${buildingId}`, { headers: { Authorization: `Bearer ${token}` } });
      setMessage('Building removed!');
      setWorldObjects(prev => prev.filter(obj => obj.buildingId !== buildingId));
      setTimeout(() => setMessage(''), 2000);
    } catch (e) {
      setMessage('Could not remove building');
      setTimeout(() => setMessage(''), 2000);
    }
  };
  
  // Drag state for building placement
  const [draggingBuilding, setDraggingBuilding] = useState(null);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [movingBuilding, setMovingBuilding] = useState(null);
  
  // Onboarding tour state
  const [showTour, setShowTour] = useState(() => !localStorage.getItem('typetown_tour_completed'));
  const handleTourComplete = useCallback(() => { localStorage.setItem('typetown_tour_completed', 'true'); setShowTour(false); }, []);
  
  const moveBuilding = async (buildingId, newWorldX, newWorldY) => {
    if (!buildingId) return;
    try {
      const token = localStorage.getItem('typetown_token');
      const building = worldObjects.find(obj => obj.buildingId === buildingId);
      if (!building) return;
      await axios.delete(`${API}/island/building/${buildingId}`, { headers: { Authorization: `Bearer ${token}` } });
      const buildX = Math.round(newWorldX / 40);
      const buildY = Math.round((newWorldY + 150) / 40);
      const res = await axios.post(`${API}/island/building`, { building_type: building.type, x: buildX, y: buildY }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.building) {
        setMessage(`📍 ${BUILDINGS[building.type]?.name} moved!`);
        setWorldObjects(prev => prev.map(obj => obj.buildingId === buildingId ? { ...obj, x: newWorldX, y: newWorldY, buildingId: res.data.building.id } : obj));
      }
      setTimeout(() => setMessage(''), 2000);
    } catch (e) {
      const errorMsg = e.response?.data?.detail || 'Could not move building';
      setMessage(`${errorMsg}`);
      fetchIsland();
      setTimeout(() => setMessage(''), 2000);
    }
  };
  
  const handleDragStart = (buildingType, e) => {
    e.dataTransfer.setData('building', buildingType);
    e.dataTransfer.setData('isNew', 'true');
    setDraggingBuilding(buildingType);
    setSelectedBuilding(buildingType);
    setMovingBuilding(null);
  };
  
  const handleExistingBuildingDragStart = (obj, e) => {
    e.stopPropagation();
    e.dataTransfer.setData('buildingId', obj.buildingId);
    e.dataTransfer.setData('building', obj.type);
    e.dataTransfer.setData('isNew', 'false');
    setMovingBuilding({ buildingId: obj.buildingId, type: obj.type, originalX: obj.x, originalY: obj.y });
    setDraggingBuilding(obj.type);
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    setDragPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    const buildingType = e.dataTransfer.getData('building');
    const isNew = e.dataTransfer.getData('isNew') === 'true';
    const existingBuildingId = e.dataTransfer.getData('buildingId');
    if (!buildingType) { setDraggingBuilding(null); setMovingBuilding(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const dropX = e.clientX - rect.left - rect.width / 2;
    const dropY = e.clientY - rect.top - rect.height * 0.85;
    const worldX = playerPos.x + dropX;
    const worldY = playerPos.y + dropY;
    if (isNew) { setSelectedBuilding(buildingType); placeBuilding(worldX, worldY); }
    else if (existingBuildingId) { moveBuilding(existingBuildingId, worldX, worldY); }
    setDraggingBuilding(null);
    setMovingBuilding(null);
  };
  
  const handleWorldClick = (e) => {
    if (!showBuildMenu || !selectedBuilding) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left - rect.width / 2;
    const clickY = e.clientY - rect.top - rect.height / 2;
    const worldX = playerPos.x + clickX;
    const worldY = playerPos.y + clickY;
    placeBuilding(worldX, worldY);
  };
  
  // === LOADING STATE ===
  
  if (loading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-b from-sky-200 via-emerald-100 to-green-200 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-bounce">🌍</div>
          <div className="text-mint-green text-2xl font-display font-bold animate-pulse">Loading TypeTown...</div>
        </div>
      </div>
    );
  }
  
  // === RENDER CALCULATIONS ===
  
  const levelInfo = island?.level_info || { stage: 1, level: 1, stage_name: 'Campsite' };
  const renderDistance = 4000;
  const playerStage = levelInfo.stage || 1;
  const screenH = typeof window !== 'undefined' ? window.innerHeight : 800;
  const screenW = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const GRASS_TOP = screenH * 0.44;
  const GRASS_BOTTOM = screenH * 0.80;
  
  const renderObjects = worldObjects.map(obj => {
    const relX = obj.x - playerPos.x;
    const relY = obj.y - playerPos.y;
    const maxDistance = renderDistance;
    const minDistance = 5;
    const isNpc = obj.type === 'npc';
    const isUserBuilding = !!obj.buildingId;
    const behindCullDistance = (isNpc || isUserBuilding) ? 300 : 150;
    if (relY > behindCullDistance || relY < -maxDistance) return null;
    const rawDepth = (-relY + minDistance) / maxDistance;
    const depth = Math.max(0.05, Math.min(0.95, rawDepth));
    const screenY = Math.round(GRASS_BOTTOM - (GRASS_BOTTOM - GRASS_TOP) * depth);
    const npcScale = 1.4 - depth * 0.8;
    const envScale = 0.9 - depth * 0.4;
    const scale = isNpc ? Math.max(0.4, npcScale) : Math.max(0.35, envScale);
    const perspectiveX = 1.2 - depth * 0.5;
    const screenX = Math.round(screenW / 2 + relX * perspectiveX);
    const zIndex = Math.floor(150 - depth * 140);
    return { ...obj, screenX, screenY, scale, opacity: 1, zIndex, depth };
  }).filter(Boolean).sort((a, b) => b.depth - a.depth);
  
  const userBuildings = island?.buildings || [];
  const currentLevel = levelInfo?.level || 1;
  const unlockedBuildingTypes = getUnlockedBuildings(currentLevel);
  
  // Helper to get object render size
  const getObjectSize = (obj) => {
    if (obj.type === 'tent') return { width: 140, height: 120 };
    if (obj.type === 'hut') return { width: 150, height: 160 };
    if (obj.type === 'house') return { width: 170, height: 170 };
    if (obj.type === 'office') return { width: 150, height: 200 };
    if (obj.type === 'npc') return { width: 80, height: 130 };
    if (obj.buildingId) {
      const buildingLevel = TYPE_TO_LEVEL_MAP[obj.type] || 1;
      if (buildingLevel >= 81) return { width: 220, height: 240 };
      if (buildingLevel >= 61) return { width: 200, height: 220 };
      if (buildingLevel >= 41) return { width: 180, height: 200 };
      if (buildingLevel >= 21) return { width: 160, height: 180 };
      return { width: 140, height: 150 };
    }
    return { width: 120, height: 120 };
  };
  
  // === RENDER ===
  
  return (
    <div
      className="fixed inset-0 overflow-hidden select-none"
      tabIndex={0}
      onClick={handleWorldClick}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      style={{ cursor: showBuildMenu && selectedBuilding ? 'crosshair' : 'default' }}
    >
      <Sky timeOfDay={timeOfDay} playerX={playerPos.x} weather={weather} />
      <Ground playerX={playerPos.x} playerY={playerPos.y} />
      
      {/* World Objects */}
      {renderObjects.map(obj => {
        const isUserBuilding = !!obj.buildingId;
        const isNpc = obj.type === 'npc';
        const isClickable = isUserBuilding || isNpc;
        const isBeingMoved = obj.buildingId != null && movingBuilding?.buildingId === obj.buildingId;
        const size = getObjectSize(obj);
        const scaledWidth = size.width * obj.scale;
        const scaledHeight = size.height * obj.scale;
        
        return (
          <div
            key={obj.id}
            className={`absolute ${isUserBuilding ? 'group cursor-pointer hover:z-[300]' : ''} ${isNpc ? 'cursor-pointer' : ''} ${isBeingMoved ? 'opacity-40' : ''}`}
            draggable={isUserBuilding}
            onDragStart={(e) => isUserBuilding && handleExistingBuildingDragStart(obj, e)}
            onMouseDown={(e) => { if (isUserBuilding) e.stopPropagation(); }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (isNpc && obj.npcId) {
                // Everything is free - no upgrade needed
                if (false) {
                  setShowUpgradePrompt(true);
                } else {
                  setActiveNpc(obj.npcId);
                }
              }
              else if (isUserBuilding) {
                // Open building typing challenge
                setActiveBuilding({ id: obj.buildingId, type: obj.type, name: obj.type });
              }
            }}
            style={{
              left: obj.screenX, top: obj.screenY, width: scaledWidth, height: scaledHeight,
              zIndex: obj.zIndex + (isUserBuilding ? 50 : 0),
              pointerEvents: isClickable ? 'auto' : 'none',
              cursor: isClickable ? 'pointer' : 'default',
              transform: 'translate(-50%, -100%)',
            }}
            data-building-id={obj.buildingId}
            data-testid={obj.buildingId ? `building-${obj.buildingId}` : (isNpc ? `npc-wrapper-${obj.npcId}` : undefined)}
          >
            <WorldObject type={obj.type} screenX={0} screenY={0} scale={obj.scale} opacity={obj.opacity} zIndex={1} npcId={obj.npcId} buildingId={obj.buildingId} onRemove={removeBuilding} />
          </div>
        );
      })}
      
      
      {/* Drag preview */}
      {draggingBuilding && (
        <div className="fixed pointer-events-none opacity-60" style={{ left: dragPosition.x - 40, top: dragPosition.y - 40, zIndex: 500 }}>
          <div className="text-6xl">{BUILDINGS[draggingBuilding]?.icon || '🏠'}</div>
        </div>
      )}
      
      {/* Player Character */}
      <div className={`absolute left-1/2 -translate-x-1/2 ${!isMoving ? 'animate-idle-breathe' : ''}`} style={{ bottom: '4%', zIndex: 200 }}>
        <BlockyCharacter3D
          shirtColor={pixelData.shirtColor} pantsColor={pixelData.pantsColor} shoes={pixelData.shoes}
          direction={direction} isMoving={isMoving}
          size={typeof window !== 'undefined' && window.innerWidth < 640 ? 120 : 160}
          skinTone={pixelData.skinTone} hairStyle={pixelData.hairStyle} hairColor={pixelData.hairColor}
          eyeStyle={pixelData.eyeStyle} mouthStyle={pixelData.mouthStyle} accessory={pixelData.accessory}
        />
      </div>
      
      {/* HUD - Top */}
      <div className="absolute top-2 sm:top-4 left-2 sm:left-4 right-2 sm:right-4 flex justify-between items-start z-[260]">
        <Link to="/dashboard" className="bg-white/90 backdrop-blur-sm px-2 sm:px-4 py-1.5 sm:py-2 rounded-xl sm:rounded-2xl text-text-dark flex items-center gap-1 sm:gap-2 hover:bg-white transition-all shadow-md border-2 border-mint-green/30 font-semibold text-sm sm:text-base">
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-mint-green" /> <span className="hidden sm:inline">Back</span>
        </Link>
        <div className="flex gap-1.5 sm:gap-3 flex-wrap justify-end">
          <div className="bg-white/90 backdrop-blur-sm px-2 sm:px-5 py-1.5 sm:py-2 rounded-xl sm:rounded-2xl shadow-md border-2 border-sunny-yellow/30 flex items-center gap-1 sm:gap-2">
            <span className="text-lg sm:text-2xl">{STAGE_ICONS[playerStage]?.icon || '🏕️'}</span>
            <div className="hidden sm:block">
              <span className="font-display font-bold" style={{ color: STAGE_ICONS[playerStage]?.color || '#FF6B6B' }}>{STAGE_ICONS[playerStage]?.name || 'Newcomer'}</span>
              <span className="text-text-medium ml-2 font-semibold">Level {levelInfo.level}</span>
            </div>
            <span className="sm:hidden text-xs font-bold" style={{ color: STAGE_ICONS[playerStage]?.color || '#FF6B6B' }}>L{levelInfo.level}</span>
          </div>
          <Link to="/leaderboard" className="bg-white/90 backdrop-blur-sm px-2 sm:px-4 py-1.5 sm:py-2 rounded-xl sm:rounded-2xl shadow-md border-2 border-lavender/30 flex items-center gap-1 sm:gap-2 hover:bg-lavender/10 transition-all" data-testid="leaderboard-btn">
            <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-lavender" /><span className="hidden sm:inline font-semibold text-lavender">Rankings</span>
          </Link>
          <div data-testid="coins-display" className="bg-white/90 backdrop-blur-sm px-2 sm:px-5 py-1.5 sm:py-2 rounded-xl sm:rounded-2xl text-sunny-yellow flex items-center gap-1 sm:gap-2 shadow-md border-2 border-sunny-yellow/30">
            <Star className="w-4 h-4 sm:w-5 sm:h-5 fill-sunny-yellow" /> <span className="font-display font-bold text-sm sm:text-lg">{island?.credits || 0}</span>
          </div>
        </div>
      </div>
      
      {/* Message */}
      {message && (
        <div className="absolute top-16 sm:top-20 left-1/2 -translate-x-1/2 z-[260] bg-gradient-to-r from-mint-green to-sunny-yellow text-white px-4 sm:px-8 py-2 sm:py-4 rounded-xl sm:rounded-2xl font-display font-bold text-sm sm:text-xl shadow-lg shadow-mint-green/30 animate-bounce border-2 border-white/50 max-w-[90%] text-center">
          {message}
        </div>
      )}
      
      {/* Mini Map - always visible on desktop, toggleable on mobile */}
      <div className="hidden sm:block">
        <MiniMap playerPos={playerPos} playerStage={playerStage} worldObjects={worldObjects} setPlayerPos={setPlayerPos} setMessage={setMessage} />
      </div>
      {/* Mobile map toggle button */}
      <button
        onClick={() => setShowMobileMap(prev => !prev)}
        className="absolute bottom-28 left-2 z-[271] w-10 h-10 bg-white/90 backdrop-blur-md rounded-lg border-2 border-mint-green/50 flex items-center justify-center shadow-lg sm:hidden"
        data-testid="toggle-map-btn"
      >
        <Map className="w-5 h-5 text-mint-green" />
      </button>
      {showMobileMap && (
        <div className="sm:hidden">
          <MiniMap playerPos={playerPos} playerStage={playerStage} worldObjects={worldObjects} setPlayerPos={setPlayerPos} setMessage={setMessage} />
        </div>
      )}
      
      {/* Movement hint (desktop only) */}
      <div className="absolute bottom-4 left-48 z-[260] bg-white/90 backdrop-blur-sm px-4 py-2 rounded-2xl text-text-medium text-sm shadow-md border-2 border-soft-gray hidden md:block">
        <div className="flex items-center gap-2">
          <span className="font-semibold">Move:</span>
          <kbd className="bg-mint-green/20 text-mint-green px-2 py-0.5 rounded-lg font-bold">W</kbd>
          <kbd className="bg-mint-green/20 text-mint-green px-2 py-0.5 rounded-lg font-bold">A</kbd>
          <kbd className="bg-mint-green/20 text-mint-green px-2 py-0.5 rounded-lg font-bold">S</kbd>
          <kbd className="bg-mint-green/20 text-mint-green px-2 py-0.5 rounded-lg font-bold">D</kbd>
        </div>
      </div>
      
      {/* Mobile Touch Controls */}
      <MobileDPad keysPressed={keysPressed} setIsMoving={setIsMoving} setDirection={setDirection} />
      
      {/* Bottom Controls - Higher z-index on mobile to be above D-pad */}
      <div className="absolute bottom-2 md:bottom-44 lg:bottom-24 right-2 sm:right-4 left-2 sm:left-auto z-[280] flex gap-2 sm:gap-3 justify-center sm:justify-end pointer-events-auto">
        <button
          onClick={() => setShowBuildMenu(!showBuildMenu)}
          data-testid="build-button"
          className={`px-3 sm:px-6 py-2 sm:py-3 rounded-xl sm:rounded-2xl font-bold flex items-center gap-1 sm:gap-2 transition-all shadow-md text-sm sm:text-base ${showBuildMenu ? 'bg-sunny-yellow text-text-dark border-2 border-sunny-yellow' : 'bg-white/90 text-text-dark hover:bg-sunny-yellow/20 border-2 border-sunny-yellow/50'}`}
        >
          <Home className="w-4 h-4 sm:w-5 sm:h-5" /> <span className="hidden sm:inline">BUILD</span><span className="sm:hidden">🏠</span>
        </button>
        <button
          onClick={openStageSelector}
          data-testid="typing-button"
          className="bg-gradient-to-r from-mint-green to-sky-blue hover:from-sky-blue hover:to-mint-green text-white px-4 sm:px-8 py-2 sm:py-3 rounded-xl sm:rounded-2xl font-bold flex items-center gap-1 sm:gap-2 transition-all hover:scale-105 shadow-lg border-2 border-white/30 text-sm sm:text-base"
        >
          <Keyboard className="w-4 h-4 sm:w-5 sm:h-5" /> TYPE <span className="hidden sm:inline">LEVEL</span> {levelInfo.level}
        </button>
      </div>
      
      {/* Progress bar */}
      <div className="absolute bottom-14 sm:bottom-4 left-1/2 -translate-x-1/2 z-[250] w-72 sm:w-96 max-w-[90vw]">
        <div className="bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl p-1.5 sm:p-2 shadow-md border-2 border-soft-gray">
          <div className="flex items-center gap-2 sm:gap-3 px-2 sm:px-3">
            <span className="text-text-medium text-xs sm:text-sm font-semibold">S{levelInfo.stage}</span>
            <div className="flex-1 h-2 sm:h-3 bg-soft-gray rounded-full overflow-hidden shadow-inner">
              <div className="h-full bg-gradient-to-r from-mint-green via-sunny-yellow to-coral-orange transition-all rounded-full" style={{ width: `${(levelInfo.level % 100 || 100)}%` }} />
            </div>
            <span className="text-text-medium text-xs sm:text-sm font-semibold">{levelInfo.level}/100</span>
          </div>
        </div>
      </div>
      
      {/* Build Menu */}
      {showBuildMenu && (
        <BuildMenu island={island} currentLevel={currentLevel} selectedBuilding={selectedBuilding} setSelectedBuilding={setSelectedBuilding} setShowBuildMenu={setShowBuildMenu} setDraggingBuilding={setDraggingBuilding} handleDragStart={handleDragStart} placeBuilding={placeBuilding} getUnlockedBuildings={getUnlockedBuildings} ALL_BUILDINGS={ALL_BUILDINGS} BUILDINGS={BUILDINGS} />
      )}
      
      {/* Modals */}
      {showChallenge && currentChallenge && (
        <TypingChallenge phrase={currentChallenge.phrase} levelInfo={currentChallenge.level_info} phrasesTypedInLevel={island?.phrases_in_current_level || 0} phrasesPerLevel={10} onComplete={completeChallenge} onCancel={() => setShowChallenge(false)} voiceEnabled={user?.voice_enabled !== false} playerVoice={user?.character_voice || 'alice'} />
      )}
      {showStageQuiz && <StageCompletionQuiz stage={quizStage} onComplete={handleQuizComplete} onClose={() => setShowStageQuiz(false)} voiceEnabled={user?.voice_enabled !== false} playerVoice={user?.character_voice || 'alice'} />}
      {showLevelCelebration && <LevelCelebration data={levelCelebrationData} onClose={closeLevelCelebration} onNextLevel={nextLevelFromCelebration} voiceEnabled={user?.voice_enabled !== false} playerVoice={user?.character_voice || 'alice'} />}
      {showStageCelebration && <StageCelebration data={stageCelebrationData} onClose={closeStageCelebration} voiceEnabled={user?.voice_enabled !== false} playerVoice={user?.character_voice || 'alice'} />}
      {activeNpc && (
        <NPCInteraction
          npc={activeNpc}
          onClose={() => setActiveNpc(null)}
          onCreditsEarned={(credits) => { setMessage(`+${credits} Coins from talking!`); setTimeout(() => setMessage(''), 3000); fetchIsland(); }}
          voiceEnabled={user?.voice_enabled !== false}
        />
      )}
      {activeBuilding && (
        <BuildingChallenge
          building={activeBuilding}
          onComplete={() => { setActiveBuilding(null); fetchIsland(); }}
          onClose={() => setActiveBuilding(null)}
          voiceEnabled={user?.voice_enabled !== false}
          playerVoice={user?.character_voice || 'alice'}
        />
      )}
      {showTour && <OnboardingTour onComplete={handleTourComplete} onSkip={handleTourComplete} />}
      {/* Upgrade prompt removed - everything is free for life */}
      
      {/* Building Options Modal */}
      {selectedBuildingForRemoval && (
        <div 
          className="fixed inset-0 bg-black/50 z-[400] flex items-center justify-center p-4"
          onClick={() => setSelectedBuildingForRemoval(null)}
          data-testid="building-modal-overlay"
        >
          <div 
            className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
            onClick={e => e.stopPropagation()}
            data-testid="building-modal"
          >
            <div className="text-center mb-4">
              <div className="text-5xl mb-2">{BUILDINGS[selectedBuildingForRemoval.type]?.icon || '🏠'}</div>
              <h3 className="font-display font-bold text-xl text-text-dark">
                {BUILDINGS[selectedBuildingForRemoval.type]?.name || 'Building'}
              </h3>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => {
                  removeBuilding(selectedBuildingForRemoval.id);
                  setSelectedBuildingForRemoval(null);
                }}
                className="w-full py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-colors"
                data-testid="building-remove-btn"
              >
                🗑️ Remove Building
              </button>
              <button
                onClick={() => setSelectedBuildingForRemoval(null)}
                className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-text-dark rounded-xl font-medium transition-colors"
                data-testid="building-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Stage Selector Modal */}
      {showStageSelector && (
        <div 
          className="fixed inset-0 bg-black/50 z-[400] flex items-center justify-center p-4"
          onClick={() => setShowStageSelector(false)}
          data-testid="stage-selector-overlay"
        >
          <div 
            className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl"
            onClick={e => e.stopPropagation()}
            data-testid="stage-selector-modal"
          >
            <h2 className="font-display font-bold text-2xl text-text-dark text-center mb-2">
              Choose Your Challenge
            </h2>
            <p className="text-text-medium text-center mb-6 text-sm">
              Practice earlier stages to earn more coins!
            </p>
            
            <div className="space-y-3">
              {/* Current stage button */}
              <button
                onClick={() => startChallenge(null)}
                className="w-full p-4 bg-gradient-to-r from-mint-green to-sky-blue text-white rounded-2xl font-bold flex items-center gap-4 hover:scale-[1.02] transition-transform"
                data-testid="stage-current-btn"
              >
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl">
                  {STAGE_ICONS[levelInfo.stage]?.icon || '🏕️'}
                </div>
                <div className="flex-1 text-left">
                  <div className="text-lg">Current Stage</div>
                  <div className="text-white/80 text-sm">Stage {levelInfo.stage}: {levelInfo.stage_name}</div>
                </div>
                <div className="text-white/80">→</div>
              </button>
              
              {/* Practice stages */}
              {levelInfo.stage > 1 && (
                <div className="pt-2 border-t border-gray-200">
                  <div className="text-xs text-text-medium mb-2 uppercase tracking-wide">Practice Earlier Stages</div>
                  {Array.from({ length: levelInfo.stage - 1 }, (_, i) => i + 1).map(stage => (
                    <button
                      key={stage}
                      onClick={() => startChallenge(stage)}
                      className="w-full p-3 bg-gray-50 hover:bg-sunny-yellow/20 border-2 border-gray-200 hover:border-sunny-yellow rounded-xl font-medium flex items-center gap-3 mb-2 transition-colors"
                      data-testid={`stage-practice-${stage}-btn`}
                    >
                      <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center text-xl">
                        {STAGE_ICONS[stage]?.icon || '🏕️'}
                      </div>
                      <div className="flex-1 text-left">
                        <div className="text-text-dark">Stage {stage}</div>
                        <div className="text-text-medium text-xs">{STAGE_ICONS[stage]?.name || 'Practice'}</div>
                      </div>
                      <div className="text-sunny-yellow text-sm font-bold">+2 coins/phrase</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <button
              onClick={() => setShowStageSelector(false)}
              className="w-full mt-4 py-2 text-text-medium hover:text-text-dark text-sm font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default IslandPage;
